import { A11yModule } from '@angular/cdk/a11y';
import { ESCAPE } from '@angular/cdk/keycodes';
import { Overlay, OverlayPositionBuilder, OverlayRef, ScrollStrategy } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EnterBottom } from '@wheere-front/shared-util';
import { filter, tap } from 'rxjs';

const DialogConfig = {
  backdropClass: 'bg-gray-900 bg-opacity-40',
};

type DialogType = 'alertdialog' | 'dialog';

@Component({
  selector: 'lib-dialog-content',
  standalone: true,
  template: ` <ng-content></ng-content>`,
  host: {
    class: 'outline-0',
    '[attr.tabindex]': '0',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogContentComponent {}

@Component({
  selector: 'lib-dialog',
  standalone: true,
  exportAs: 'libDialog',
  imports: [DialogContentComponent, A11yModule],
  animations: [EnterBottom()],
  host: {
    '[attr.role]': 'type()',
  },
  template: `
    <ng-template #content>
      @defer (when isOpen()) {
        <lib-dialog-content @EnterBottom cdkTrapFocus>
          <ng-content></ng-content>
        </lib-dialog-content>
      }
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogComponent implements OnInit, OnDestroy {
  type = input<DialogType>('dialog');
  /**
   * Sur téléphone (< 640 px, évalué à l'ouverture), la fenêtre s'ancre en bas de l'écran,
   * pleine largeur : une feuille du bas au lieu d'une boîte centrée. Opt-in, défaut inchangé.
   */
  sheetOnMobile = input(false, { transform: booleanAttribute });
  /**
   * Tant que vrai, ni le clic sur le fond ni Échap ne ferment la fenêtre — pour une opération
   * longue en cours (ex. création d'un prospect, ~30-60 s). `hide()` reste possible par code.
   */
  locked = input(false, { transform: booleanAttribute });
  closed = output<void>();

  templateContent = viewChild.required<TemplateRef<DialogContentComponent>>('content');

  isOpen = signal(false);

  private overlayRef: OverlayRef | undefined = undefined;
  /**
   * `show()` demandé avant `ngOnInit` (l'overlay n'existe pas encore). Depuis Angular 19, l'`effect`
   * d'un composant parent s'exécute AVANT la mise à jour de son gabarit, donc avant le `ngOnInit`
   * de ce dialogue : une modale dont `visible` vaut vrai dès la création appelait `show()` trop tôt
   * et ne s'ouvrait jamais. La demande est mémorisée et honorée à l'initialisation.
   */
  private showRequestedBeforeInit = false;
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private destroyRef = inject(DestroyRef);
  private overlayPositionBuilder = inject(OverlayPositionBuilder);

  ngOnInit() {
    const positionStrategy = this.overlayPositionBuilder.global().centerHorizontally().centerVertically();

    const scrollStrategy: ScrollStrategy = this.overlay.scrollStrategies.block();

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy,
      hasBackdrop: true,
      backdropClass: DialogConfig.backdropClass.split(' ').concat(this.type() === 'dialog' ? 'cursor-pointer' : ''),
    });

    if (this.type() === 'dialog') {
      this.overlayRef
        .backdropClick()
        .pipe(
          filter(() => !this.locked()),
          tap(() => this.hide()),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();

      this.overlayRef
        .keydownEvents()
        .pipe(
          filter(e => e.keyCode === ESCAPE && !this.locked()),
          tap(() => this.hide()),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    }

    if (this.showRequestedBeforeInit) {
      this.showRequestedBeforeInit = false;
      this.show();
    }
  }

  ngOnDestroy() {
    this.overlayRef?.dispose();
  }

  show() {
    if (!this.overlayRef) {
      this.showRequestedBeforeInit = true;
      return;
    }
    if (this.templateContent() && this.overlayRef && !this.overlayRef.hasAttached()) {
      if (this.sheetOnMobile()) {
        const wide = typeof window !== 'undefined' && !!window.matchMedia?.('(min-width: 640px)')?.matches;
        const position = this.overlayPositionBuilder.global().centerHorizontally();
        this.overlayRef.updatePositionStrategy(wide ? position.centerVertically() : position.bottom('0'));
      }
      this.overlayRef.attach(new TemplatePortal(this.templateContent(), this.viewContainerRef));
      this.isOpen.set(true);
    }
  }

  hide() {
    this.showRequestedBeforeInit = false;
    if (this.overlayRef?.hasAttached()) {
      this.overlayRef.detach();
      this.closed.emit();
      this.isOpen.set(false);
    }
  }
}
